"use client";

import { useEffect, useRef } from "react";
import { gsap } from "@/lib/gsap";

interface Feature {
  number?: string;
  title: string;
  description: string;
}

interface FeaturesCardsProps {
  features: Feature[];
}

export default function FeaturesCards({ features }: FeaturesCardsProps) {
  const cardRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      cardRef.current.forEach((card, index) => {
        if (card) {
          gsap.fromTo(
            card,
            {
              opacity: 0,
              y: 50,
              scale: 0.94,
              filter: "blur(8px)",
            },
            {
              opacity: 1,
              y: 0,
              scale: 1,
              filter: "blur(0px)",
              duration: 1,
              ease: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
              scrollTrigger: {
                trigger: card,
                start: "top 90%",
                end: "top 45%",
                toggleActions: "play none none reverse",
              },
              delay: index * 0.3,
            }
          );
        }
      });
    });

    return () => ctx.revert();
  }, []);

  const addToRefs = (el: HTMLDivElement | null, index: number) => {
    if (el) {
      cardRef.current[index] = el;
    }
  };

  return (
    <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
      {features.map((feature, index) => (
        <div
          key={feature.number || index}
          ref={(el) => addToRefs(el, index)}
          className="scroll-into-view"
        >
          {feature.number && (
            <p className="text-sm font-semibold text-theme-primary">
              {feature.number}
            </p>
          )}
          <h3 className="mt-4 text-xl font-semibold">{feature.title}</h3>
          <p className="mt-4 text-white font-semibold">{feature.description}</p>
        </div>
      ))}
    </div>
  );
}
